import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import UserList from './UserList';
import type { PositionBadge } from './UserList';
import UserForm from './UserForm';
import { userService, User } from '../../services/userService';
import postingService from '../../services/postingService';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

const RBAC_ACTIVITY = 'ORG_USERS';

interface UserManagementProps {
    compact?: boolean;
}

type StatusFilter = 'active' | 'inactive';

const UserManagement: React.FC<UserManagementProps> = ({ compact = false }) => {
    const viewPerm    = usePermission(RBAC_ACTIVITY, 'view');
    const canView     = viewPerm.allowed;
    const modifyPerm  = usePermission(RBAC_ACTIVITY, 'view+modify');
    const canModify   = modifyPerm.allowed;
    const permLoading = modifyPerm.loading;
    const approvePerm = usePermission(RBAC_ACTIVITY, 'view+modify+approve');
    const canApprove  = approvePerm.allowed;

    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [userPostingsMap, setUserPostingsMap] = useState<Record<number, PositionBadge[]>>({});
    const [workflowOpenFor, setWorkflowOpenFor] = useState<User | null>(null);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, pageSize, statusFilter]);

    const loadData = useCallback(async () => {
        if (!canView) {
            setUsers([]);
            setTotalCount(0);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const result = await userService.getAllUsers({
                search: searchTerm || undefined,
                status: statusFilter === 'active' ? 'ACTIVE' : 'INACTIVE',
                page: currentPage,
                limit: pageSize,
            });
            setUsers(result.data);
            setTotalCount(result.meta.total);
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to load users' });
        } finally {
            setLoading(false);
        }
    }, [searchTerm, statusFilter, currentPage, pageSize, canView]);

    const loadPostings = useCallback(async () => {
        try {
            const res = await postingService.getAll({ action: 'JOIN', limit: 100 });
            const map: Record<number, PositionBadge[]> = {};
            for (const p of res.data) {
                if (!map[p.user_id]) map[p.user_id] = [];
                map[p.user_id].push({
                    position_name: p.position_name || 'Unknown',
                    position_code: p.position_code
                });
            }
            setUserPostingsMap(map);
        } catch (err) {
            console.error('Failed to load postings for badges:', err);
        }
    }, []);

    useEffect(() => {
        const debounce = setTimeout(() => { loadData(); loadPostings(); }, 300);
        return () => clearTimeout(debounce);
    }, [loadData, loadPostings]);

    const handleOpenAdd = () => {
        if (!canModify) { setToast({ type: 'error', message: modifyPerm.message || 'Not authorised' }); return; }
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (user: User) => {
        // Entity Super Admins are not editable here, but the record is still
        // viewable — UserForm renders read-only (no fillable fields, no action
        // buttons) when is_entity_admin is set. Mirrors the approved-lock view.
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleSaveUser = (t?: ToastMessage) => {
        setIsModalOpen(false);
        setEditingUser(null);
        loadData();
        if (t) setToast(t);
    };

    const getUserDisplayName = (u: User) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.login_id || `User #${u.id}`;

    const displayUsers = users.map(u => ({
        ...u,
        name: getUserDisplayName(u),
        email: u.email || '',
        phone: u.phone || '',
        employeeId: u.login_id || '',
        status: (u.is_active === 1 ? 'Active' : 'Inactive') as 'Active' | 'Inactive',
        isApproved: u.is_approved === 1,
        workflow_state: u.workflow_state,
        roles: u.roles || [],
        roleCount: (u as any).role_count ?? 0,
        avatar: '',
        positions: userPostingsMap[u.id] || [],
        is_entity_admin: (u as any).is_entity_admin || 0,
    }));

    if (!viewPerm.loading && !canView) {
        return (
            <div className="netbody-card flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound
                    title="Not authorised"
                    message={viewPerm.message || 'You are not authorised to view Users. Please contact your Super Admin.'}
                />
            </div>
        );
    }

    return (
        <div className={`netbody-card flex flex-col h-full ${compact ? '' : 'min-h-[500px]'}`}>

            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'active' ? 'ACTIVE' : 'INACTIVE'}
                    onChange={next => setStatusFilter(next === 'ACTIVE' ? 'active' : 'inactive')}
                    noun="users"
                    testId="BTN_user_status_toggle"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Find by name, email or ID…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_user_search"
                    />
                    <Button variant="fill" icon={Plus} onClick={handleOpenAdd} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_user_list_create">
                        Create User
                    </Button>
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden flex flex-col">
                    <div className="flex-1 overflow-y-auto">
                        <UserList
                            users={displayUsers}
                            loading={loading}
                            page={currentPage}
                            pageSize={pageSize}
                            onEdit={(u: any) => {
                                const original = users.find(orig => orig.id === u.id);
                                if (original) handleOpenEdit(original);
                            }}
                            onDotClick={(u: any) => {
                                const original = users.find(orig => orig.id === u.id);
                                if (original) setWorkflowOpenFor(original);
                            }}
                        />
                    </div>
                    <Pagination
                        page={currentPage}
                        pageSize={pageSize}
                        totalRecords={totalCount}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        recordLabel="users"
                    />
                </div>
            </div>

            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={() => setWorkflowOpenFor(null)}
                kind="usr_user"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={loadData}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />

            <UserForm
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingUser(null); }}
                user={editingUser}
                onSave={handleSaveUser}
                canModify={canModify}
                canApprove={canApprove}
                modifyDeniedMessage={modifyPerm.message || ''}
                approveDeniedMessage={approvePerm.message || ''}
            />
        </div>
    );
};

export default UserManagement;
